import React, { useContext, useEffect, useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@crowd/ui-kit";
import classNames from "classnames";
import MetaDecorator from "../../presentational/MetaDecorator/MetaDecorator";
import EventsTrackWrapperClick from "../../containers/EventsTrack/wrappers/EventsTrackWrapperClick";
import { RootState } from "../../../types/State.interface";

import {
  AuthAction,
  AuthActionParam,
  AuthActionType,
  ComponentType,
} from "../../../services/sudirService";
import { AppContext } from "../../Root";
import {
  declOfNum,
  findUrlParam,
  formatNumber,
  removeUrlParam,
} from "../../../utils";

import {
  CompletedIdeasWrapperHandle,
  CompletedIdeasWrapper,
} from "../../containers/CompletedIdeasWrapper/CompletedIdeasWrapper";
import IdeaService from "../../../services/ideaService";
import { ApiStatusCode } from "../../../types/Common.interface";
import { showErrorToast } from "../../../store/actions/ToastActions";
import { setAppColor } from "../../../store/actions/LayoutActions";
import { AppColorsEnum } from "../../App.interface";
import FourColumnLoader from "../../presentational/Loaders/FourColumnLoader/FourColumnLoader";
import FadeIn from "../../presentational/FadeIn/FadeIn";
import EventsTrackParentIdContext from "../../containers/EventsTrack/EventsTrackParentIdContext";
import UserDetailsModal from "../../containers/Modals/UserDetailsModal/UserDetailsModal";

import styles from "./IdeasPage.module.scss";
import { useForceUpdate } from "../../../hooks/useForceUpdate";
import { UserIdeaCountDto } from "../../../types/Idea.interface";
import {
  selectIsUserReady,
  selectUserDetails,
} from "../../../store/selectors/profile";
import PageHeading from "../../presentational/PageHeading/PageHeading";
import Text from "../../presentational/Typography/Text";
import AppLink from "../../presentational/Controls/AppLink/AppLink";
import Tooltip from "../../presentational/Controls/Tooltip/Tooltip";
import { useIsMobile, useIsTablet } from "../../../hooks/useMedia";
import IdeasPhotoBrowser from "../../containers/Modals/IdeasPhotoBrowser/IdeasPhotoBrowser";
import IdeasFilter, { IdeasFilterProps } from "./IdeasFilter/IdeasFilter";
import { selectIdeasTags } from "../../../store/selectors/idea";
import { getIdeaTags } from "../../../store/actions/IdeasActions";
import MediaIdeaService from "../../../services/mediaIdeaService";
import { mapMediaCardToIdea } from "../../../utils/mediaCards.utils";
import PhotoIdeaForm from "../../containers/Forms/PhotoIdeaForm/PhotoIdeaForm";

const roundFloorCountPhotos = (num: number) => {
  const divider = num > 1000 ? 1000 : num > 100 ? 100 : num > 10 ? 10 : 1;
  return Math.floor(num / divider) * divider;
};

const maxBalls = 1000;

const IdeasPage = () => {
  const forceUpdate = useForceUpdate();
  const appContext = useContext(AppContext);
  const dispatch = useDispatch();
  const history = useHistory<{ item: ReturnType<typeof mapMediaCardToIdea> }>();
  const user = useSelector(selectUserDetails);
  const stats = useSelector((state: RootState) => state.totalStatistics.stats);
  const ideasTags = useSelector(selectIdeasTags);

  const [ideasList, setIdeasList] = useState(null);
  const wrapperRef = useRef<CompletedIdeasWrapperHandle>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [ideaCount, setIdeaCount] = useState<UserIdeaCountDto>();
  const [currentIdea, setCurrentIdea] = useState(null);
  const [ideaModalOpen, setIdeaModalOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [authorModalOpen, setAuthorModalOpen] = useState(false);
  const isPhone = useIsMobile();
  const isTablet = useIsTablet();
  const [page, setPage] = useState(0);
  const isUserReady = useSelector(selectIsUserReady);

  // фильтры
  const [filter, setFilter] = useState<IdeasFilterProps["filter"]>(() => {
    let selectedTags: string[] = null;
    const decoded = decodeURI(history.location.search);
    if (decoded) {
      const params = findUrlParam("tags", decoded);
      const data = params && params.split(",").filter(Boolean);
      selectedTags = data?.length ? data : null;
    }

    return {
      search: "",
      district: null,
      tags: selectedTags,
      type: null,
      sort: null,
    };
  });

  useEffect(() => {
    dispatch(setAppColor(AppColorsEnum.WHITE));
    IdeaService.getCount()
      .then(({ data }) => setIdeaCount(data))
      .catch(console.log);

    if (!ideasTags) {
      dispatch(getIdeaTags());
    }
  }, []);

  useEffect(() => {
    const idea = history.location.state?.item;
    if (idea) {
      addTag(idea.tag.name);
    }
  }, [history]);

  useEffect(() => {
    if (isUserReady && ideasList?.length) {
      checkActionsAfterAuth();
    }
  }, [isUserReady, ideasList]);

  const loadIdeaFromUrl = async () => {
    const ideaId = findUrlParam("id", history.location.search);
    const isMedia = findUrlParam("isMedia", history.location.search) === "true";

    if (!ideaId) return;

    try {
      const idea = await loadIdeaById(ideaId, isMedia);
      _setIdea(isMedia ? mapMediaCardToIdea(idea) : idea, page);
    } catch (e) {
      console.error("Ошибка при загрузке идеи из URL:", e);
    }
  };

  useEffect(() => {
    loadIdeaFromUrl();
  }, []);

  const checkActionsAfterAuth = () => {
    const actions = appContext.sudirService.getActions(ComponentType.IDEA_PAGE);

    if (!actions.length || !ideasList?.length) return;

    const likeAction = actions.find((act) => act.type === AuthActionType.LIKE);

    if (likeAction) {
      const idea = ideasList.find((i) => i.id === likeAction.args.ideaId);
      if (idea) handleLike(idea);
    }

    const openFormAction = actions.find(
      (act) => act.type === AuthActionType.OPEN_IDEAS_MODAL
    );

    if (openFormAction) {
      setIdeaModalOpen(true);
    }
  };

  const loadIdeaById = async (id: string, isMedia: boolean = false) => {
    try {
      if (isMedia) {
        const { status, message, data } = await MediaIdeaService.getById(id);
        if (status === ApiStatusCode.OK) {
          return { ...data, isMediaCard: true };
        }
        throw new Error(message);
      } else {
        const { status, message, data } = await IdeaService.ideaById(id);
        if (status === ApiStatusCode.OK) {
          return data;
        }
        throw new Error(message);
      }
    } catch (e: any) {
      dispatch(showErrorToast(e.message));
    }
  };

  const uploadPhoto = () => {
    if (!user.loggedIn) {
      const thisContainerData: AuthActionParam = {
        component: ComponentType.IDEA_PAGE,
        type: AuthActionType.OPEN_IDEAS_MODAL,
        args: {},
      };

      const action1 = new AuthAction(thisContainerData);
      return appContext.sudirService.authWithActions([action1]);
    }

    setIdeaModalOpen(true);
  };

  const addTag = (tag) =>
    setFilter({ ...filter, tags: [...(filter.tags || []), tag] });

  const onCardTagClick = (idea) => {
    if (!filter.tags?.includes(idea.tag?.name)) {
      addTag(idea.tag?.name);
    }
  };

  const handleLike = (idea) => {
    if (!user.loggedIn) {
      const thisContainerData: AuthActionParam = {
        component: ComponentType.IDEA_PAGE,
        type: AuthActionType.LIKE,
        args: {
          ideaId: idea.id,
        },
      };

      const action1 = new AuthAction(thisContainerData);
      return appContext.sudirService.authWithActions([action1]);
    }

    !idea.liked && like(idea);
  };

  const like = async (idea) => {
    try {
      const { status, message } = idea.isMediaCard
        ? await MediaIdeaService.like(idea.mediaIdeaId)
        : await IdeaService.like(idea.id);

      if (status === ApiStatusCode.OK) {
        idea.liked = true;
        idea.rating++;
      } else {
        throw new Error(message);
      }
    } catch (e: any) {
      dispatch(showErrorToast(e.message));
    } finally {
      forceUpdate();
    }
  };

  const canSlidePrev = () => {
    if (!ideasList || !currentIdea) return false;
    const index = ideasList.findIndex((i) => i.id === currentIdea.id);
    return index > 0;
  };

  const canSlideNext = () => {
    if (!ideasList || !currentIdea) return false;
    const idx = ideasList.findIndex((i) => i.id === currentIdea.id);
    return idx < ideasList.length - 1 || !!wrapperRef.current;
  };

  const onSlidePrev = () => {
    if (!ideasList || !currentIdea) return;

    const index = ideasList.findIndex((i) => i.id === currentIdea.id);
    if (index === -1 || index === 0) return;

    const prevIdea = ideasList[index - 1];
    _setIdea(prevIdea, page);
  };

  const onSlideNext = async () => {
    if (!ideasList || !currentIdea) return;

    const ids = ideasList.map((i) => i.id);
    const idx = ids.lastIndexOf(currentIdea.id);

    if (idx < ideasList.length - 1) {
      _setIdea(ideasList[idx + 1], page);
      return;
    }

    if (wrapperRef.current) {
      const newBatch = await wrapperRef.current.loadPage(false);
      if (newBatch.length) {
        setIdeasList((prev) => [...(prev ?? []), ...newBatch]);
        setPage(page + 1);
        _setIdea(newBatch[0], page + 1);
      }
    }
  };

  const _setIdea = (idea, index) => {
    const searchParams = new URLSearchParams({
      page: String(index + 1),
      id: idea.isMediaCard ? idea.mediaIdeaId : idea.id,
    });

    if (idea.isMediaCard) {
      searchParams.set("isMedia", "true");
    }

    const pageUrl = `/ideas?${searchParams.toString()}`;

    window.history.replaceState("", "", pageUrl);

    setCurrentIdea(idea);
    setPhotoViewerOpen(true);
  };

  const renderStats = () => {
    if (!ideaCount) {
      return null;
    }

    const published = formatNumber(roundFloorCountPhotos(ideaCount.published));

    return (
      <div className={styles.stats}>
        {ideaCount.published > 0 && (
          <div className={styles["stats-item"]}>
            <Text size="hb" className={styles["stats-item-num"]}>
              {`>${published}`}
            </Text>
            <Text
              size={isTablet && !isPhone ? 4 : 3}
              align="center"
              className={styles["stats-item-hint"]}
            >
              {declOfNum(published, [
                "фотография опубликована",
                "фотографии опубликовано",
                "фотографий опубликовано",
              ])}
            </Text>
          </div>
        )}
        {ideaCount.ideaItemPublished > 0 && (
          <div className={styles["stats-item"]}>
            <AppLink
              to="/photos#ideas"
              size="hb"
              className={styles["stats-item-num"]}
            >
              {ideaCount.ideaItemPublished}{" "}
              {declOfNum(ideaCount.ideaItemPublished, ["идея", "идеи", "идей"])}
            </AppLink>
            <Text
              size={isTablet && !isPhone ? 4 : 3}
              align="center"
              className={styles["stats-item-hint"]}
            >
              {declOfNum(ideaCount.ideaItemPublished, [
                "открыта",
                "открыты",
                "открыты",
              ])}{" "}
              для подтверждения
            </Text>
          </div>
        )}
        <div
          className={classNames(
            styles["stats-item"],
            styles["stats-item_points"]
          )}
        >
          <Text size="hb" className={styles["stats-item-num"]}>
            {user?.loggedIn
              ? `${maxBalls - (ideaCount.userPoints || 0)} из`
              : "До"}{" "}
            {maxBalls}
            <img
              className={styles["icon-crystall"]}
              src={require("../../../assets/crystal.svg").default}
              alt=""
            />
          </Text>
          <Text
            size={isTablet && !isPhone ? 4 : 3}
            align="center"
            className={styles["stats-item-hint"]}
          >
            {user?.loggedIn
              ? "баллов доступно для получения"
              : "баллов вы сможете получить, подтверждая идеи"}
            <Tooltip
              idx="ideas-page-points"
              text="Вы можете получить до 1000 баллов в месяц, подтверждая идеи фотографиями"
              place="left"
              outerClassName={styles["stats-item-tip"]}
            >
              <i className="promo-icon-84" />
            </Tooltip>
          </Text>
        </div>
      </div>
    );
  };

  const renderFormButton = () => (
    <EventsTrackWrapperClick
      id={["SEND_FORM", 182]}
      replace={["Загрузить фото"]}
    >
      <Button
        type="filled"
        size="l"
        text="Загрузить фото"
        onClick={uploadPhoto}
        classNames={styles.btn}
      />
    </EventsTrackWrapperClick>
  );

  const renderFilter = () => (
    <IdeasFilter filter={filter} onChange={setFilter} isDisabled={isLoading} />
  );

  const getPageDescription = () => (
    <>
      В галерее вы можете увидеть, как предложения москвичей становятся
      реальностью и влияют на развитие города. Участвуйте в жизни столицы и
      подтверждайте реализацию идей, предложенных на платформе! Ознакомьтесь с{" "}
      <a
        href={`${window.location.origin}/photos`}
        target="_blank"
        data-click-id='["BLOCK_SECTION_DESC", 183]'
        data-replace='["основными условиями"]'
        rel="noreferrer"
      >
        основными условиями
      </a>{" "}
      и{" "}
      <a
        href={`${window.location.origin}/moderation`}
        target="_blank"
        data-click-id='["BLOCK_SECTION_DESC", 183]'
        data-replace='["правилами модерации"]'
        rel="noreferrer"
      >
        правилами модерации
      </a>
      .
    </>
  );

  const renderList = () => (
    <EventsTrackParentIdContext.Provider value="BLOCK_PHOTO_CARD">
      <div>
        <CompletedIdeasWrapper
          ref={wrapperRef}
          onCardClick={_setIdea}
          onAuthorClick={(idea) => {
            setCurrentIdea(idea);
            setAuthorModalOpen(true);
          }}
          onTagClick={onCardTagClick}
          onLike={handleLike}
          onLoadEnd={(newIdeas, pageIndex) => {
            setIdeasList((prev) => {
              const updated = [...(prev ?? []), ...newIdeas];
              return updated;
            });
            setPage(pageIndex);
          }}
          filter={filter}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
        />
      </div>
    </EventsTrackParentIdContext.Provider>
  );

  const isLoaded = () => !!stats && !!ideasTags;
  if (!isLoaded()) return <FourColumnLoader />;

  const title = "«Город идей» — Галерея реализованных идей";
  const description =
    "Ознакомьтесь с фотографиями реализованных идей, которые предлагали жители на платформе «Город идей».";
  return (
    <FadeIn>
      <section className={styles.ideas}>
        <MetaDecorator
          title={`${title}. Страница ${page + 1}`}
          description={description}
          opengraph={{ title, description }}
        />

        <PageHeading>Галерея реализованных идей</PageHeading>
        <Text size={isTablet ? 2 : 1}>{getPageDescription()}</Text>
        {renderStats()}
        <div className={styles["ideas-wrapper"]}>
          {renderFormButton()}
          {renderFilter()}
          {renderList()}
        </div>
        {ideaModalOpen && (
          <PhotoIdeaForm isOpen onClose={() => setIdeaModalOpen(false)} />
        )}
        {currentIdea && authorModalOpen && (
          <UserDetailsModal
            idea={currentIdea}
            isOpen={authorModalOpen}
            onClose={() => setAuthorModalOpen(false)}
          />
        )}
        {currentIdea && (
          <IdeasPhotoBrowser
            idea={currentIdea}
            isOpened={photoViewerOpen}
            onClose={() => {
              const pageUrl = `/ideas`;
              window.history.replaceState("", "", pageUrl);
              removeUrlParam("id", history);
              removeUrlParam("isMedia", history);
              setPhotoViewerOpen(false);
            }}
            canSlideNext={canSlideNext()}
            canSlidePrev={canSlidePrev()}
            onSlideNext={onSlideNext}
            onSlidePrev={onSlidePrev}
            onAuthorClick={(idea) => {
              setCurrentIdea(idea);
              setAuthorModalOpen(true);
            }}
            onLike={handleLike}
          />
        )}
      </section>
    </FadeIn>
  );
};

export default IdeasPage;
